import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import moment from "moment";
import {
  ArrowRightIcon,
  CommentComposer,
  CommentIcon,
  CommentList,
} from "../../StrategyCanvas/StrategyCanvasHelpers";
import useExecutionBlueprintStore from "../_redux/ExecutionBlueprintStore";
import useExecutionBlueprintReviewStore, {
  useExecutionBlueprintReviewStoreSelector,
} from "./_redux/ExecutionBlueprintReviewStore";
import { COMMENT_SCOPE, REVIEW_STATUS } from "../_redux/ExecutionBlueprintUrls";
import CanvasBand from "../../StrategyCanvas/CanvasBand/CanvasBand";
import { getSectionColor, useInitialSectionFocus } from "./ExecutionBlueprintReviewHelpers";
import SummaryGroups, { FilterChips } from "../../StrategyCanvas/SummaryPanel/SummaryPanel";
import {
  ITEM_KIND,
  buildReviewSummary,
  countItems,
  countWhere,
  filterGroups,
} from "./ExecutionBlueprintReviewSummary";
import ItemRow from "./ItemRow";
import CompleteReviewModal from "./CompleteReviewModal";
import LiveReviewStepperModal from "./LiveReviewStepperModal";
import { EXECUTION_BLUEPRINT_LIVE_REVIEW } from "../../../../data/RouteUrls";
import { StandardTooltip } from "../../../shared/StandardTooltip/StandardTooltip";
// Reuses Canvas's own Review Mode layout classes (.scrm-*, .rm-*) directly -
// see this file's own comment block below for why this screen has no
// StrategyCanvasStructure.js-style band/group config to invent (D4).
import "../../StrategyCanvas/StrategyCanvasReview/ReviewMode/StrategyCanvasReviewMode.scss";
import "./ExecutionBlueprintReview.scss";

const selectReviewByUniqueId = (state) => state.reviewByUniqueId;
const selectReviewLoading = (state) => state.reviewLoading;
const selectReviewSubmittedByUniqueId = (state) =>
  state.reviewSubmittedByUniqueId;

// `blueprint_state`'s value once the coach sends the review to the CEO. Not a
// guess: 08-fe-handoff.md §1 enumerates the whole pipeline
// (SYNTHESIS_PROCESSING → … → COACH_REVIEW → COACH_REVIEW_SUBMITTED →
// LIVE_REVIEW_IN_PROGRESS → LOCKED), and it happens to be the same string
// Canvas matches on its own `canvas_state` - different field, same value.
//
// Kept local rather than added to ExecutionBlueprintUrls.js because this is
// the only value of the enum any coach-side code needs; the rest describe CEO
// authoring stages this repo never reads. Note §4's rule that
// `blueprint_state` must never be read for *stage display* (that's the
// dashboard's last-non-null-milestone walk) - this isn't stage display, it's a
// permission check, so the two must not be unified.
const BLUEPRINT_STATE_REVIEW_SUBMITTED = "COACH_REVIEW_SUBMITTED";

// Shimmer placeholder shown the first time a coach opens Review Mode, while
// GET :uniqueId/review is in flight - mirrors Canvas's own
// ReviewModeSkeleton (StrategyCanvasReviewMode.js), one section fewer since
// EXW has 4 fixed sections rather than an open-ended band count.
const ReviewModeSkeleton = () => (
  <div className="scrm-container">
    <div className="scrm-left">
      {Array.from({ length: 4 }).map((_, sectionIndex) => (
        <div className="rm-section" key={sectionIndex}>
          <div className="rm-section-heading">
            <span className="rm-skel rm-skel-bar" />
            <span className="rm-skel rm-skel-title" />
          </div>
          <div className="rm-rows">
            {Array.from({ length: 3 }).map((__, rowIndex) => (
              <div className="rm-skel rm-skel-row" key={rowIndex} />
            ))}
          </div>
          <div className="rm-section-divider" />
        </div>
      ))}
    </div>

    <div className="scrm-right">
      <div className="rm-summary-header">
        <span className="rm-skel rm-skel-title" style={{ width: 120 }} />
      </div>
      <div className="rm-summary-header-divider" />
      <div className="rm-right-body">
        <div className="rm-summary-pills">
          <span className="rm-skel rm-skel-pill" />
          <span className="rm-skel rm-skel-pill" />
          <span className="rm-skel rm-skel-pill" />
        </div>
        {Array.from({ length: 3 }).map((_, index) => (
          <span className="rm-skel rm-skel-card" key={index} />
        ))}
      </div>
    </div>
  </div>
);

// Top-level "Overall Assessment" comment list + composer (scope_type
// "overall"). Mirrors Canvas's OverallCommentPanel exactly - kept separate
// since it has no heading/trigger row of its own.
const OverallCommentPanel = ({ comments, onAdd, onDelete, placeholder, readOnly }) => {
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [isCommenting, setIsCommenting] = useState(false);

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    const saved = await onAdd(draft.trim());
    setSaving(false);
    if (saved) {
      setDraft("");
      setIsCommenting(false);
    }
  };

  const handleDelete = async (commentId) => {
    setDeletingId(commentId);
    await onDelete(commentId);
    setDeletingId(null);
  };

  const showTrigger = !readOnly && comments.length === 0 && !isCommenting;

  return (
    <>
      {showTrigger && (
        <button
          type="button"
          className="rm-overall-trigger"
          onClick={() => setIsCommenting(true)}
        >
          <CommentIcon size={12} />
          Add your overall assessment for the CEO
        </button>
      )}

      {comments.length > 0 && (
        <CommentList
          comments={comments}
          onDelete={handleDelete}
          deletingId={deletingId}
          readOnly={readOnly}
          badge="Overall assessment"
        />
      )}

      {!readOnly && comments.length === 0 && isCommenting && (
        <CommentComposer
          className="rm-section-comment-composer"
          value={draft}
          onChange={setDraft}
          onSave={handleSave}
          saving={saving}
          placeholder={placeholder}
        />
      )}
    </>
  );
};

// Section heading + its own comment panel - mirrors Canvas's
// SectionCommentHeading, but always in the "bare" mode Canvas reserves for a
// band whose label already names its single section (Growth/Core/DNA there,
// `rm-section-heading--bare` + no bar/title). EXW's bands and sections are
// always 1:1 with an identical label - CanvasBand already prints
// `section.label` in its own header immediately above this, so repeating it
// here would just print the same title twice in a row (this is what a
// coach would actually see if this weren't bare: "Financial WIG" / "Financial
// WIG").
const SectionCommentHeading = ({ section, uniqueId, readOnly }) => {
  const [isCommenting, setIsCommenting] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const comments = section.comments || [];
  const showTrigger = !readOnly && comments.length === 0;

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    const saved = await useExecutionBlueprintReviewStore
      .getState()
      .addComment(uniqueId, {
        scopeType: COMMENT_SCOPE.SECTION,
        scopeKey: section.section_key,
        commentText: draft.trim(),
      });
    setSaving(false);
    if (saved) {
      setDraft("");
      setIsCommenting(false);
    }
  };

  const handleDelete = async (commentId) => {
    setDeletingId(commentId);
    await useExecutionBlueprintReviewStore.getState().deleteComment(uniqueId, commentId);
    setDeletingId(null);
  };

  return (
    <>
      {(showTrigger || isCommenting) && (
        <div className="rm-section-heading rm-section-heading--bare">
          {isCommenting && (
            <span className="rm-section-heading-meta">
              section comment (optional)
            </span>
          )}
          {showTrigger && (
            <button
              className="rm-comment-link rm-section-comment-trigger"
              onClick={() => setIsCommenting((prev) => !prev)}
            >
              <CommentIcon size={12} />
              Add a comment for the CEO
            </button>
          )}
        </div>
      )}

      {(isCommenting || comments.length > 0) && (
        <div className="rm-section-comment-panel">
          {comments.length > 0 && (
            <CommentList
              comments={comments}
              onDelete={handleDelete}
              deletingId={deletingId}
              readOnly={readOnly}
              className="rm-section-comment-list"
            />
          )}

          {!readOnly && isCommenting && (
            <CommentComposer
              className="rm-section-comment-composer"
              value={draft}
              onChange={setDraft}
              onSave={handleSave}
              saving={saving}
              placeholder="Summarise your overall read on this dimension for the CEO…"
            />
          )}
        </div>
      )}
    </>
  );
};

const FooterActionButton = ({ label, onClick, disabled, disabledReason }) => {
  const button = (
    <button
      type="button"
      className="rm-complete-btn"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
    >
      {label}
      <ArrowRightIcon />
    </button>
  );

  if (!disabled || !disabledReason) return button;

  return (
    <StandardTooltip tooltTipContent={disabledReason}>
      <span className="rm-btn-tooltip-wrap">{button}</span>
    </StandardTooltip>
  );
};

// Review Mode - left column renders the real review payload (sections ->
// items, each an expandable ItemRow with Accept / To-discuss / comments).
// Right column is the Review Summary panel, built from the same
// sections[].items[] data. No band/group tree here unlike Canvas's
// ReviewMode: EXW's 4 sections already ARE the top-level grouping the API
// returns, pre-ordered by `sequence` - StrategyCanvasStructure.js exists
// only to flatten Canvas's 7 ungrouped layers into 4 bands, a problem this
// payload doesn't have (D4).
//
// The mode toggle and the "How review works" modal live one level up in
// ExecutionBlueprintReview.js, which owns the header this body sits under.
// The Start Live Review CTA does NOT: it belongs to this screen's footer, in
// the same slot "Complete review & send to CEO" occupies before submission -
// see the footer below, and StrategyCanvasReviewMode.js for the original.
const ExecutionBlueprintReviewMode = ({ uniqueId }) => {
  const [summaryFilter, setSummaryFilter] = useState("all");
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showStartLiveReviewModal, setShowStartLiveReviewModal] =
    useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Local rather than read off the store's `startingLiveReview`, matching
  // `submitting` above: the button that owns the click owns its own spinner, so
  // a second surface starting a live review can't disable this one.
  const [startingLiveReview, setStartingLiveReview] = useState(false);
  const history = useHistory();

  const reviewByUniqueId = useExecutionBlueprintReviewStoreSelector(
    selectReviewByUniqueId,
  );
  const reviewLoading = useExecutionBlueprintReviewStoreSelector(
    selectReviewLoading,
  );
  const reviewSubmittedByUniqueId = useExecutionBlueprintReviewStoreSelector(
    selectReviewSubmittedByUniqueId,
  );
  const review = reviewByUniqueId[uniqueId];

  // ── Read-only once the review has gone to the CEO ────────────────────────
  // Canvas reads this off `canvas_state === "COACH_REVIEW_SUBMITTED"`; the
  // EXW equivalent is `blueprint_state`, which the user confirmed carries the
  // same value at this point. The local flag is OR'd in so the screen flips
  // the instant the submit resolves, without waiting for a refetch to prove
  // it - the review payload in the store is the pre-submit copy until then.
  //
  // Everything already decided still renders. What goes away is every control
  // that would call an API: accept/to-discuss, comment delete, and all three
  // comment composers. Hidden rather than disabled, matching Canvas - a
  // greyed-out Accept next to an already-accepted item reads as a broken
  // control rather than a finished decision.
  const submittedLocally = !!reviewSubmittedByUniqueId[uniqueId]?.submitted;
  const isSubmitted =
    review?.blueprint_state === BLUEPRINT_STATE_REVIEW_SUBMITTED ||
    submittedLocally;
  const submittedMeeting = reviewSubmittedByUniqueId[uniqueId];

  useEffect(() => {
    useExecutionBlueprintReviewStore.getState().fetchReview(uniqueId);
    // eslint-disable-next-line
  }, [uniqueId]);

  // Sorted by `sequence` before the early loading return so the hook order
  // stays stable - same reasoning Canvas's ReviewMode uses for `bands`.
  const sections = useMemo(
    () =>
      [...(review?.sections || [])].sort(
        (a, b) => (a.sequence ?? 0) - (b.sequence ?? 0),
      ),
    [review],
  );

  const {
    collapsedSections,
    setCollapsedSections,
    expandedItemId,
    setExpandedItemId,
  } = useInitialSectionFocus(sections);

  // ── The Complete Review gate ─────────────────────────────────────────────
  // ⚠️ THE BUTTON IS NEVER DISABLED (2026-09-03, on instruction). It used to
  // carry `disabled={review.reviewed_count < review.total_items}` with a
  // tooltip explaining the shortfall.
  //
  // Why click-time validation is better here, not just different: a disabled
  // button tells the coach they cannot proceed but not WHICH of twenty rows is
  // holding them up — the tooltip could only ever give them a score. Letting
  // the click land means the check can answer the actual question, by taking
  // them to the first unmarked row and marking all of them.
  //
  // ⚠️ Unmarked is `!item.review_status`, matching the per-section
  // `reviewedCount` a few lines below. It is deliberately NOT derived from
  // `review.reviewed_count`: that is a server-computed total, so it can say
  // "3 remaining" without being able to say which three.
  const pendingItems = useMemo(() => {
    const out = [];
    sections.forEach((section) => {
      (section.items || []).forEach((item) => {
        if (!item.review_status) {
          out.push({
            id: item.execution_blueprint_item_id,
            sectionKey: section.section_key,
          });
        }
      });
    });
    return out;
  }, [sections]);

  // The ids to redden and flash. Empty until the coach actually tries to
  // submit — see `ItemRow`'s `pending` note for why that matters.
  const [flaggedPendingIds, setFlaggedPendingIds] = useState(() => new Set());
  const [pendingFlashKey, setPendingFlashKey] = useState(0);
  const reviewCanvasRef = useRef(null);
  // The ids this click flashes, frozen at click time. See the flash effect.
  const flashTargetsRef = useRef([]);

  // Scroll to the first flagged row and flash all of them. Mirrors the live
  // review's agenda-target effect (`eblr-target-flash`) — same 1600ms, same
  // reason: the view otherwise just moves and the coach has to work out which
  // of several near-identical rows they were sent to.
  //
  // ⚠️ KEYED ON `pendingFlashKey` ALONE, AND THAT IS THE POINT (fixed
  // 2026-09-03). It also depended on `expandedItemId` and `collapsedSections`,
  // copied from the live review's version of this effect — which meant that
  // after a blocked submit, every subsequent expand or collapse RE-RAN it:
  // the canvas jumped back to the first pending row and everything flashed
  // again, while the coach was trying to work through the rows. Scroll and
  // flash are a response to one click and must happen exactly once per click.
  //
  // Those deps were never needed. The three `set*` calls in
  // `handleCompleteClick` are batched into one render, and effects run after
  // that render has committed — so the sections are already expanded and the
  // rows already in the DOM by the time this queries for them.
  //
  // The ids come from a REF rather than from `flaggedPendingIds` state for the
  // same reason: that set shrinks as the coach marks rows, and reading it here
  // would put it in the dep array and reintroduce the re-firing.
  useEffect(() => {
    if (!pendingFlashKey) return undefined;
    const root = reviewCanvasRef.current;
    if (!root) return undefined;

    const nodes = flashTargetsRef.current
      .map((id) => root.querySelector(`[data-review-target="item-${id}"]`))
      .filter(Boolean);
    if (nodes.length === 0) return undefined;

    // The FIRST in document order — `pendingItems` is built by walking
    // sections in sequence, so index 0 is the topmost pending row. Being sent
    // to the third one while two sit unseen above it would read as the wrong
    // row.
    nodes[0].scrollIntoView({ behavior: "smooth", block: "center" });
    nodes.forEach((node) => node.classList.add("rr-row-flash"));
    const timer = setTimeout(
      () => nodes.forEach((node) => node.classList.remove("rr-row-flash")),
      1600,
    );
    return () => clearTimeout(timer);
  }, [pendingFlashKey]);

  // Clear the flags as the coach works through them, so a row stops being red
  // the moment it is marked rather than staying red until the next submit.
  useEffect(() => {
    if (flaggedPendingIds.size === 0) return;
    const stillPending = new Set(pendingItems.map((entry) => entry.id));
    const next = [...flaggedPendingIds].filter((id) => stillPending.has(id));
    if (next.length !== flaggedPendingIds.size) {
      setFlaggedPendingIds(new Set(next));
    }
  }, [pendingItems, flaggedPendingIds]);

  if (reviewLoading || !review) {
    return <ReviewModeSkeleton />;
  }

  const toggleSection = (sectionKey) =>
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionKey)) next.delete(sectionKey);
      else next.add(sectionKey);
      return next;
    });

  // Clicking Complete Review. Either the review is finished and the confirm
  // dialog opens, or the coach is shown exactly what is missing.
  const handleCompleteClick = () => {
    if (pendingItems.length === 0) {
      setShowCompleteModal(true);
      return;
    }

    const ids = pendingItems.map((entry) => entry.id);
    setFlaggedPendingIds(new Set(ids));
    // What this click flashes. A ref, so the effect below can read it without
    // taking a dependency on state that changes as the coach works — see its
    // note.
    flashTargetsRef.current = ids;

    // ⚠️ EVERY section holding a pending row opens, not just the first one's
    // (2026-09-03, on instruction). Opening only the first left the rest of the
    // flagged rows reddened and flashing *inside collapsed bands*, where the
    // coach could not see any of it — so the count said eight and the screen
    // showed one.
    const pendingSections = new Set(
      pendingItems.map((entry) => entry.sectionKey),
    );
    setCollapsedSections((prev) => {
      const next = new Set(
        [...prev].filter((sectionKey) => !pendingSections.has(sectionKey)),
      );
      // Same-size means nothing was collapsed that needed opening. Returning
      // `prev` keeps the reference stable and skips a pointless re-render.
      return next.size === prev.size ? prev : next;
    });

    // Only the topmost pending row expands — `expandedItemId` holds one id, so
    // this is an accordion by construction and there is no "expand them all".
    // It is also the right one: it is where the scroll lands.
    setExpandedItemId(pendingItems[0].id);

    // A counter rather than a boolean: clicking again with rows still pending
    // has to re-run the scroll and re-fire the flash, and a boolean already
    // `true` would do neither.
    setPendingFlashKey((key) => key + 1);
  };


  const summaryGroups = buildReviewSummary(review);
  const summaryTotal = countItems(summaryGroups);
  const summaryDiscuss = countWhere(
    summaryGroups,
    (item) => item.status === REVIEW_STATUS.TO_DISCUSS,
  );
  const summaryWithComments = countWhere(
    summaryGroups,
    (item) => item.comments.length > 0,
  );
  const summarySectionComments = countWhere(
    summaryGroups,
    (item) => item.kind === ITEM_KIND.SECTION,
  );

  const SUMMARY_FILTERS = {
    all: () => true,
    discuss: (item) => item.status === REVIEW_STATUS.TO_DISCUSS,
    comments: (item) => item.comments.length > 0,
    sections: (item) => item.kind === ITEM_KIND.SECTION,
  };
  const visibleSummaryGroups = filterGroups(
    summaryGroups,
    SUMMARY_FILTERS[summaryFilter] || SUMMARY_FILTERS.all,
  );

  const handleSubmitReview = async (body) => {
    setSubmitting(true);
    const result = await useExecutionBlueprintReviewStore
      .getState()
      .submitReview(uniqueId, body);
    setSubmitting(false);
    if (result) {
      setShowCompleteModal(false);
      // Deliberately stays on this screen rather than returning to the
      // dashboard (which is what it used to do, back when a submitted review
      // left nothing further to do here). Live Review is started from this
      // screen, and submitting is exactly what unlocks that button - sending
      // the coach away would mean navigating back to find it. The dashboard
      // is still refreshed so its stage/CTA are right when they do return.
      useExecutionBlueprintStore.getState().fetchDashboard();
      // Picks up the new `blueprint_state` so the read-only flip survives a
      // reload, rather than resting on the local flag alone.
      useExecutionBlueprintReviewStore
        .getState()
        .fetchReview(uniqueId, { force: true });
    }
  };

  // POST live-review/start, then navigate. Real as of 2026-08-27 - this used to
  // write a localStorage flag because there was no endpoint (see the store).
  //
  // Navigation waits for the POST rather than running alongside it, which is a
  // change in feel worth being deliberate about: the live review screen opens a
  // socket and immediately emits `join_live_review`, and joining a session the
  // server hasn't started yet fails with `live_review_not_started` and bounces
  // the coach straight back. Optimistic navigation would turn a slow request
  // into a visible round trip through a screen that rejects them.
  //
  // A 409 resolves as success (the store explains why: it almost always means
  // already-started), and if it genuinely meant "too early", the gate on the
  // live review screen catches it and returns them here.
  const handleStartLiveReview = async () => {
    setStartingLiveReview(true);
    const result = await useExecutionBlueprintReviewStore
      .getState()
      .startLiveReview(uniqueId);
    setStartingLiveReview(false);
    if (!result?.ok) return; // Error already surfaced by the store.

    setShowStartLiveReviewModal(false);
    // The dashboard's stage/CTA are now stale - this blueprint just moved to
    // "Complete Live Review". Fire-and-forget: nothing on this path reads it.
    useExecutionBlueprintStore.getState().fetchDashboard();
    history.push(
      EXECUTION_BLUEPRINT_LIVE_REVIEW.replace(":blueprintId", uniqueId),
    );
  };

  // Reads back the meeting the coach set in the complete-review modal, if they
  // set one - scheduling is optional there, so this is often absent.
  const scheduledLine = (() => {
    if (!submittedMeeting?.scheduled_date) return "";
    const stamp = submittedMeeting.scheduled_time
      ? `${submittedMeeting.scheduled_date} ${submittedMeeting.scheduled_time}`
      : submittedMeeting.scheduled_date;
    const parsed = moment(
      stamp,
      [
        "YYYY-MM-DD HH:mm:ss",
        "YYYY-MM-DD HH:mm",
        "YYYY-MM-DD",
        moment.ISO_8601,
      ],
      true,
    );
    // Falls back to the raw string rather than printing "Invalid date": the
    // submit response's date format is unconfirmed (COACH-API.md §5), so a
    // format this doesn't recognise is a real possibility.
    if (!parsed.isValid()) return stamp;
    return submittedMeeting.scheduled_time
      ? parsed.format("h:mm A [on] Do MMMM, YYYY.")
      : parsed.format("Do MMMM, YYYY.");
  })();

  return (
    <div className="scrm-container">
      <div className="scrm-left" ref={reviewCanvasRef}>
        {sections.map((section, index) => {
          const totalCount = section.items.length;
          const reviewedCount = section.items.filter(
            (item) => !!item.review_status,
          ).length;

          return (
            <CanvasBand
              key={section.section_key}
              label={section.label}
              color={getSectionColor(index)}
              reviewedCount={reviewedCount}
              totalCount={totalCount}
              expanded={!collapsedSections.has(section.section_key)}
              onToggle={() => toggleSection(section.section_key)}
            >
              <div className="rm-section">
                <SectionCommentHeading
                  section={section}
                  uniqueId={uniqueId}
                  readOnly={isSubmitted}
                />

                <div className="rm-rows">
                  {section.items.map((item) => (
                    <ItemRow
                      key={item.execution_blueprint_item_id}
                      uniqueId={uniqueId}
                      item={item}
                      expanded={
                        expandedItemId === item.execution_blueprint_item_id
                      }
                      onToggleExpand={() =>
                        setExpandedItemId((prev) =>
                          prev === item.execution_blueprint_item_id
                            ? null
                            : item.execution_blueprint_item_id,
                        )
                      }
                      readOnly={isSubmitted}
                      pending={flaggedPendingIds.has(
                        item.execution_blueprint_item_id,
                      )}
                    />
                  ))}
                </div>
              </div>
            </CanvasBand>
          );
        })}

        <div className="rm-overall">
          <div className="rm-overall-comments">
            <OverallCommentPanel
              comments={review.overall_comments || []}
              placeholder="Your overall assessment of the Execution Blueprint for the CEO"
              onAdd={(text) =>
                useExecutionBlueprintReviewStore.getState().addComment(uniqueId, {
                  scopeType: COMMENT_SCOPE.OVERALL,
                  scopeKey: null,
                  commentText: text,
                })
              }
              onDelete={(commentId) =>
                useExecutionBlueprintReviewStore
                  .getState()
                  .deleteComment(uniqueId, commentId)
              }
              readOnly={isSubmitted}
            />
          </div>
        </div>
      </div>

      <div className="scrm-right">
        <div className="rm-summary-header">
          <span className="rm-summary-header-title">Review Summary</span>
        </div>
        <div className="rm-summary-header-divider" />

        {summaryTotal > 0 && (
          <div className="rm-summary-filters">
            <FilterChips
              value={summaryFilter}
              onChange={setSummaryFilter}
              options={[
                { key: "all", label: "All", count: summaryTotal },
                { key: "discuss", label: "To discuss", count: summaryDiscuss },
                {
                  key: "comments",
                  label: "With comments",
                  count: summaryWithComments,
                },
                {
                  key: "sections",
                  label: "Section comments",
                  count: summarySectionComments,
                },
              ]}
            />
          </div>
        )}

        <div className="rm-right-body">
          {summaryTotal === 0 ? (
            <div className="rm-summary-empty">Your review will appear here.</div>
          ) : (
            <SummaryGroups
              groups={visibleSummaryGroups}
              emptyLabel="Nothing matches this filter."
            />
          )}
        </div>

        {/* One slot, two states - the CTA swaps in place rather than the
            footer growing a second button, exactly as Canvas does it. Once
            the review is in, "Complete review & send to CEO" has nothing
            left to do, so Start Live Review takes its place as the only
            action on the screen. */}
        <div className="rm-right-footer">
          {isSubmitted ? (
            <>
              <FooterActionButton
                label="Start Live Review"
                onClick={() => setShowStartLiveReviewModal(true)}
              />
              {scheduledLine && (
                <div className="rm-meeting-scheduled">
                  <span>You&apos;ve scheduled the live review for</span>
                  <strong>{scheduledLine}</strong>
                </div>
              )}
            </>
          ) : (
            <>
              {/* ⚠️ NO `disabled` / `disabledReason` (2026-09-03, on
                  instruction). The click is always allowed and the check
                  happens in `handleCompleteClick`, which can point at the
                  rows rather than only counting them. */}
              <FooterActionButton
                label="Complete review & send to CEO"
                onClick={handleCompleteClick}
              />
              {/* Shown only after a blocked attempt, and it counts the
                  FLAGGED rows rather than `pendingItems` — so it drops as the
                  coach marks each one and disappears when the last is done,
                  which is the acknowledgement that they have cleared it.
                  `role="alert"` because it appears in response to an action
                  and is the only text explaining why nothing happened.

                  ⚠️ REWORDED 2026-09-03. It used to end "They're highlighted
                  below", which was wrong in two ways: the rows are in the LEFT
                  panel and this note is in the right-hand footer, so "below"
                  pointed at nothing — and by the time the coach reads it the
                  canvas has already scrolled them to the first one, so saying
                  where to look is redundant. What the line has to carry is the
                  count, why the click did nothing, and what "a decision" means
                  here. */}
              {flaggedPendingIds.size > 0 && (
                <p className="rm-pending-note" role="alert">
                  {flaggedPendingIds.size === 1
                    ? "1 WIG still needs a decision before you can send this — accept it, or flag it to discuss."
                    : `${flaggedPendingIds.size} WIGs still need a decision before you can send this — accept each one, or flag it to discuss.`}
                </p>
              )}
            </>
          )}
        </div>
      </div>

      {showCompleteModal && (
        <CompleteReviewModal
          totalItems={review.total_items}
          acceptedCount={review.accepted_count}
          toDiscussCount={review.to_discuss_count}
          commentCount={review.comment_count}
          submitting={submitting}
          onCancel={() => setShowCompleteModal(false)}
          onSubmit={handleSubmitReview}
        />
      )}

      {showStartLiveReviewModal && (
        <LiveReviewStepperModal
          activeStep={1}
          onNext={handleStartLiveReview}
          onBack={() => setShowStartLiveReviewModal(false)}
          // `onNext` now performs a real POST, so the modal has to be able to
          // show that and to stop a second click sending a second request.
          // Back stays enabled: cancelling a slow start is reasonable, and the
          // request is idempotent enough that a 409 on the retry resolves.
          nextLoading={startingLiveReview}
        />
      )}
    </div>
  );
};

export default ExecutionBlueprintReviewMode;
